import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageHeader } from '@/components/layout/PageHeader';
import { ContentWrapper } from '@/components/layout/ContentWrapper';
import {
  Key, Copy, Eye, EyeOff, Plus, Trash2, RotateCcw, Shield,
  CheckCircle, AlertTriangle, Clock, ExternalLink, Code,
} from 'lucide-react';

interface ApiKeyItem {
  id: string;
  name: string;
  prefix: string;
  created: string;
  lastUsed: string;
  status: 'active' | 'revoked' | 'expired';
  type: 'embedded' | 'secret';
  permissions: string[];
}

const mockKeys: ApiKeyItem[] = [
  {
    id: 'key_1',
    name: 'Production Server',
    prefix: 'ciq_live_7kX9...mR4z',
    created: 'Jan 15, 2025',
    lastUsed: '2 hours ago',
    status: 'active',
    type: 'secret',
    permissions: ['read', 'write', 'process'],
  },
  {
    id: 'key_2',
    name: 'Development / Staging',
    prefix: 'ciq_test_3pL2...nQ8w',
    created: 'Feb 1, 2025',
    lastUsed: '5 days ago',
    status: 'active',
    type: 'secret',
    permissions: ['read', 'write', 'process'],
  },
  {
    id: 'key_3',
    name: 'Client Portal Widget',
    prefix: 'ciq_emb_9vF5...jK2x',
    created: 'Dec 10, 2024',
    lastUsed: '1 day ago',
    status: 'active',
    type: 'embedded',
    permissions: ['read'],
  },
  {
    id: 'key_4',
    name: 'Legacy Integration',
    prefix: 'ciq_live_1aB3...hY6t',
    created: 'Aug 22, 2024',
    lastUsed: 'Nov 3, 2024',
    status: 'revoked',
    type: 'secret',
    permissions: ['read', 'write'],
  },
];

const usageStats = [
  { label: 'API Calls (30 days)', value: '12,847' },
  { label: 'Avg Response Time', value: '234ms' },
  { label: 'Success Rate', value: '99.7%' },
  { label: 'Rate Limit Usage', value: '23%' },
];

const ApiKeysPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'secret' | 'embedded'>('secret');
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [showNewKey, setShowNewKey] = useState(false);

  const filteredKeys = mockKeys.filter((k) => k.type === activeTab);

  const handleCopy = (id: string, text: string) => {
    navigator.clipboard?.writeText(text).catch(() => {});
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success" className="text-xs">Active</Badge>;
      case 'revoked':
        return <Badge variant="destructive" className="text-xs">Revoked</Badge>;
      case 'expired':
        return <Badge variant="warning" className="text-xs">Expired</Badge>;
      default:
        return <Badge variant="secondary" className="text-xs">{status}</Badge>;
    }
  };

  return (
    <ContentWrapper>
      <PageHeader
        title="API Keys"
        subtitle="Manage your API keys for programmatic access to ClaimsIQ"
        breadcrumbs={[
          { label: 'API Integration' },
          { label: 'API Keys', isActive: true },
        ]}
        actions={
          <Button
            className="bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => setShowCreateModal(true)}
          >
            <Plus className="h-4 w-4 mr-2" /> Create New Key
          </Button>
        }
      />

      {/* Security Notice */}
      <Alert variant="warning" className="mb-6">
        <Shield className="h-4 w-4" />
        <AlertDescription>
          Secret API keys grant full access to your account. Never share them in client-side code, public repositories, or unencrypted channels. Use embedded keys for browser-facing integrations.
        </AlertDescription>
      </Alert>

      {/* Usage Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {usageStats.map((stat) => (
          <Card key={stat.label} className="bg-white border border-gray-200 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
              <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setActiveTab('secret')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'secret'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Key className="h-4 w-4 inline mr-2" />
          Secret Keys ({mockKeys.filter((k) => k.type === 'secret').length})
        </button>
        <button
          onClick={() => setActiveTab('embedded')}
          className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
            activeTab === 'embedded'
              ? 'bg-white text-gray-900 shadow-sm'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <Code className="h-4 w-4 inline mr-2" />
          Embedded Keys ({mockKeys.filter((k) => k.type === 'embedded').length})
        </button>
      </div>

      {/* Keys List */}
      <div className="space-y-4 mb-8">
        {filteredKeys.map((key) => (
          <Card key={key.id} className={`bg-white border shadow-sm ${key.status === 'revoked' ? 'opacity-60' : 'border-gray-200'}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{key.name}</h3>
                    {statusBadge(key.status)}
                  </div>

                  {/* Key display */}
                  <div className="flex items-center gap-2 mb-3">
                    <code className="bg-gray-100 px-3 py-1.5 rounded text-sm font-mono text-gray-700">
                      {key.prefix}
                    </code>
                    <button
                      onClick={() => handleCopy(key.id, key.prefix)}
                      className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                      title="Copy key"
                    >
                      {copiedId === key.id ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>

                  {/* Metadata */}
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Created: {key.created}
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" /> Last used: {key.lastUsed}
                    </span>
                    <span>
                      Permissions: {key.permissions.join(', ')}
                    </span>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 ml-4">
                  {key.status === 'active' && (
                    <>
                      <Button variant="outline" size="sm">
                        <RotateCcw className="h-4 w-4 mr-1" /> Rotate
                      </Button>
                      <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4 mr-1" /> Revoke
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Create Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-lg bg-white shadow-xl">
            <CardHeader className="border-b border-gray-100">
              <CardTitle className="text-lg text-gray-900">Create New API Key</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              {!showNewKey ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Name</label>
                    <input
                      type="text"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="e.g. Production Server"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Key Type</label>
                    <select className="w-full border border-gray-300 rounded-lg px-4 py-2">
                      <option value="secret">Secret Key (server-side only)</option>
                      <option value="embedded">Embedded Key (client-side safe)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
                    <div className="space-y-2">
                      {['Read (view results, check status)', 'Write (upload databases, create jobs)', 'Process (trigger processing, manage jobs)'].map((perm) => (
                        <label key={perm} className="flex items-center gap-2 text-sm text-gray-600">
                          <input type="checkbox" defaultChecked className="rounded border-gray-300" />
                          {perm}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                    <Button variant="outline" onClick={() => { setShowCreateModal(false); setNewKeyName(''); }}>
                      Cancel
                    </Button>
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => setShowNewKey(true)}
                      disabled={!newKeyName.trim()}
                    >
                      Generate Key
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Alert variant="warning" className="mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Copy your API key now. You won't be able to see it again after closing this dialog.
                    </AlertDescription>
                  </Alert>
                  <div className="bg-gray-100 rounded-lg p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-2">Your new API key:</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-sm font-mono text-gray-900 break-all">
                        ciq_live_8fGh2jKlMnOpQrStUvWxYz1234567890abcdef
                      </code>
                      <button
                        onClick={() => handleCopy('new_key', 'ciq_live_8fGh2jKlMnOpQrStUvWxYz1234567890abcdef')}
                        className="p-2 rounded hover:bg-gray-200 transition-colors flex-shrink-0"
                      >
                        {copiedId === 'new_key' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : (
                          <Copy className="h-5 w-5 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-end pt-4 border-t border-gray-100">
                    <Button
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                      onClick={() => { setShowCreateModal(false); setNewKeyName(''); setShowNewKey(false); }}
                    >
                      Done
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Quick Start Guide */}
      <Card className="bg-gray-50 border border-gray-200">
        <CardHeader className="border-b border-gray-100">
          <CardTitle className="text-base text-gray-900 flex items-center gap-2">
            <Code className="h-5 w-5 text-gray-500" /> Quick Start
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-sm text-green-400 font-mono">
{`curl -X POST https://api.claimsiq.com/v1/claims/process \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"file_url": "s3://your-bucket/data.csv", "job_type": "realpage"}'`}
            </pre>
          </div>
          <div className="flex items-center gap-4 mt-4">
            <a href="/api/documentation" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Full API Documentation
            </a>
            <a href="/api/testing" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
              <ExternalLink className="h-3 w-3" /> Testing Console
            </a>
          </div>
        </CardContent>
      </Card>
    </ContentWrapper>
  );
};

export default ApiKeysPage;